import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { API, NetworkShareDTO } from 'src/app/services/api/api';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from '../../../services/error.service';
import { UserService } from '../../../services/user.service';
import { TagService } from '../../../services/tag.service';
import { NetworkShareDTOAndTags } from '../../../models/taggable.model';
import { DiscussionService } from '../../../services/discussion.service';
import { TableWrapperOptions } from '../../../services/global/global-interfaces';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { HierarchyService } from '../../../services/transformation-helper/hierarchy.service';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { isNumberEven } from '../../../services/global/global-methods';

@Component({
  selector: 'sb-page-network-share',
  templateUrl: './network-share.component.html',
  styleUrls: ['./network-share.component.scss'],
  standalone: false,
})
export class NetworkShareComponent implements OnInit {
  private readonly hierarchy = inject(HierarchyService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly isNumberEven = isNumberEven;
  options: TableWrapperOptions;

  hierarchies: NetworkShareDTOAndTags[] = [];

  showChildren: Map<number, boolean> = new Map<number, boolean>();

  shareDragged: number;

  isDragging: boolean = false;

  loading: boolean;

  constructor(
    private readonly api: API,
    public readonly util: UtilService,
    public readonly errorHandler: ErrorService,
    public readonly userStatus: UserService,
    public readonly tagService: TagService,
    public readonly discussionService: DiscussionService,
    public readonly modal: ModalService,
    public readonly translate: TranslateService,
    public readonly permissionService: PermissionService
  ) {
    this.hierarchies = [];
  }

  ngOnInit() {
    this.api
      .ss_nm_NetworkShare_retrieveHierarchy()
      .pipe(
        take(1),
        switchMap(shares => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.NetworkShare, shares)),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: data => {
          this.hierarchies = data;
        },
        error: err => {
          this.errorHandler.errorHandler(err);
        },
      });
  }

  request(options?: TableWrapperOptions) {
    if (options) {
      this.options = options;
    }

    if (this.options?.pagination) {
      this.loading = true;
      const preconditionIds = Array.from(this.showChildren.keys());
      this.api
        .ss_nm_NetworkShare_retrieveHierarchy(
          {
            search: this.stringTransformation.encodeSearchQueryParam(false, this.options.search),
          },
          this.options.pagination
        )
        .pipe(
          take(1),
          switchMap(shares => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.NetworkShare, shares)),
          take(1),
          switchMap(shares => {
            this.hierarchies = shares; // save shares ti local variable with tags
            return this.api.ss_nm_NetworkShare_retrieveAll({ precondition: preconditionIds }); // retrieve shares for preconditionids
          }),
          take(1),
          switchMap(shares => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.NetworkShare, shares)),
          take(1),
          takeUntilDestroyed(this.destroyRef),
          finalize(() => {
            this.loading = false;
            this.isDragging = false;
          })
        )
        .subscribe(shares => {
          const map = preconditionIds.map(preconditionId => {
            return { id: preconditionId, preconditions: [] };
          });
          shares.forEach(networkShare => {
            const toAdd = map.find(tempObj => tempObj.id === networkShare.precondition?.id);
            if (toAdd?.preconditions) {
              toAdd.preconditions.push(networkShare);
            }
          });
          for (const tempObj of map) {
            const searchNetworkShare = this.hierarchy.getObjectFromHierarchyArrayById(this.hierarchies, tempObj, 'preconditionOf');

            if (searchNetworkShare) {
              searchNetworkShare.preconditionOf = tempObj.preconditions;
            }
          }
        });
    }
  }

  private updateNetworkShareHierarchy(currentNetworkShare: NetworkShareDTO): void {
    this.loading = true;
    this.api
      .ss_nm_NetworkShare_retrieveHierarchy({
        precondition: currentNetworkShare.id,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loading = false;
          this.isDragging = false;
        })
      )
      .subscribe({
        next: (e: NetworkShareDTO[]) => {
          currentNetworkShare.preconditionOf = e;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  toggleShowChildren(share: NetworkShareDTO) {
    if (this.showChildren.has(share.id)) {
      this.removeVisibility(share);
    } else {
      this.showChildren.set(share.id, true);
      this.updateNetworkShareHierarchy(share);
    }
  }

  onmousedown($event: any) {
    let target = $event.target;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    target.setAttribute('draggable', 'true');
  }

  onmouseup($event: any) {
    let target = $event.target;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    target.removeAttribute('draggable');
  }

  onDragStart($event: any) {
    $event.dataTransfer.setData('shareid', $event.target.attributes['data-share'].value);
    $event.dataTransfer.dropEffect = 'move';
    this.shareDragged = +$event.target.attributes['data-share'].value;
  }

  onDragEnd($event: any) {
    this.isDragging = false;
  }

  onDragOver($event: any) {
    let target = $event.currentTarget;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    const draggedShare: number = +this.shareDragged;
    const targetShare: number = target.attributes['data-share'] ? +target.attributes['data-share'].value : null;

    if (draggedShare === 0) {
      return;
    }

    if (draggedShare === targetShare) {
      return;
    }

    const draggedShareObj: NetworkShareDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.hierarchies,
      { id: draggedShare },
      'preconditionOf'
    );
    const targetShareObj: NetworkShareDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.hierarchies,
      { id: targetShare },
      'preconditionOf'
    );

    if (!this.allowSubDrop(draggedShareObj, targetShareObj)) {
      return;
    }

    $event.preventDefault();
  }

  allowSubDrop(draggedShareObj: NetworkShareDTO, targetShareObj: NetworkShareDTO): boolean {
    if (!targetShareObj) {
      return true;
    }
    let currentShare: NetworkShareDTO = targetShareObj;

    while (currentShare) {
      if (currentShare.id === draggedShareObj.id) {
        return false;
      }
      currentShare = this.hierarchy.getObjectFromHierarchyArrayById(this.hierarchies, currentShare.precondition, 'preconditionOf');
    }
    return true;
  }

  onDrop($event: any, index?: number) {
    let target = $event.currentTarget;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    const draggedShare: number = +this.shareDragged;
    const targetShare: number = target.attributes['data-share'] ? +target.attributes['data-share'].value : null;

    const draggedShareObj: NetworkShareDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.hierarchies,
      { id: draggedShare },
      'preconditionOf'
    );
    const targetShareObj: NetworkShareDTO = this.hierarchy.getObjectFromHierarchyArrayById(
      this.hierarchies,
      { id: targetShare },
      'preconditionOf'
    );

    this.hierarchy.removeObjectFromHierarchyArrayById(this.hierarchies, draggedShareObj, 'preconditionOf');

    if (targetShareObj) {
      if (index != null) {
        draggedShareObj.priority = index;
        targetShareObj.preconditionOf.splice(index, 0, draggedShareObj);
      } else {
        targetShareObj.preconditionOf.push(draggedShareObj);
      }
      if (!this.showChildren.has(targetShareObj.id)) {
        this.removeVisibility(targetShareObj);
      }
    } else {
      if (index != null) {
        draggedShareObj.priority = this.hierarchies[index]
          ? this.hierarchies[index].priority
          : this.hierarchies[this.hierarchies.length - 1].priority + 1;
      }
      this.hierarchies.push(draggedShareObj);
    }

    this.refreshParents();

    this.refreshShareDragged(draggedShareObj);
  }

  private removeVisibility(share: NetworkShareDTO) {
    this.showChildren.delete(share.id);
    const traversedChildren = this.traverseChildren(share, []);
    traversedChildren?.forEach(child => this.showChildren.delete(child.id));
  }

  traverseChildren(contentShare: NetworkShareDTO, result: NetworkShareDTO[]): NetworkShareDTO[] {
    if (!contentShare.preconditionOf || !Array.isArray(contentShare.preconditionOf)) {
      return result;
    }
    result.push(...contentShare.preconditionOf);
    for (const child of contentShare.preconditionOf) {
      this.traverseChildren(child, result);
    }
    return result;
  }

  refreshShareDragged(draggedShareObj: NetworkShareDTO) {
    const tempshare: NetworkShareDTO = { ...draggedShareObj };
    tempshare.preconditionOf = [];

    if (tempshare?.precondition) {
      tempshare.precondition.preconditionOf = [];
      tempshare.precondition.precondition = null;
    }

    this.api
      .ss_nm_NetworkShare_editNetworkShare(tempshare)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe(() => {
        this.request();
      });
  }

  refreshParents() {
    for (const share of this.hierarchies) {
      this.refreshParent(share, null);
    }
  }

  refreshParent(share: NetworkShareDTO, parent?: NetworkShareDTO) {
    share.precondition = parent;
    if (share.preconditionOf && Array.isArray(share.preconditionOf)) {
      for (const child of share.preconditionOf) {
        this.refreshParent(child, share);
      }
    }
  }

  deleteNetworkShare(item: NetworkShareDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.networkShare.deleteNetworkShare'),
        message: this.translate.instant('lang.networkShare.deleteNetworkShareMessage', { itemName: item.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.loading = true;
          this.api
            .ss_nm_NetworkShare_deleteById(item.id)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {
                this.request();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
                this.loading = false;
              },
            });
        }
      });
  }
}
